/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {loadAssets} from '@ion/core/src/services/Connect';
import {AssetSearchableProperty, LoadAssetsParams} from '@ion/core/src/types/services';
import {mapDataToGroups} from '../../filters/TreeFilterUtils';
import {normalizeAccountIds} from './TreeViewUtils';

export const PAGE_SIZE = 100;

interface Status {
  [groupId: string | number]: {
    completed: boolean,
    remaining?: number,
    groupKey?: string,
    loaded: number,
    page: number
  }
}

/**
 * The AssetTreeLoader provides a centralized API for loading Assets for individual
 * Groups for subsequent insertion into the CVS or HVS Trees, determining the current
 * state for each group in terms of loading assets.
 *
 * @class AssetTreeLoader
 */
class AssetTreeLoader {
  /**
   * Provides the mappings of group ids to their current asset loading status.
   * Each value represents the status of the group, and has the following form:
   *
   * loaded    : <number of assets that have currently been loaded>
   * page      : <next page to be loaded, or current page if all assets loaded>
   * completed : <a flag to indicate if asset loading has been completed>
   * groupKey  : <the tree data stucture key>
   * remaining : <the number of assets remaining to be loaded>
   *
   * @private
   */
  _status: Status = {};

  /**
   * @method loadAssets
   * @param  groupInfo {object} A group info object created via getGroupInfo
   *         which contains a groupKey and groupId property.
   */
  async loadAssets(groupId: number, groupKey: string) {
    const status = this.getGroupStatus(groupId, groupKey);
    if (!status?.completed) {
      const {pageSize, sort, filters} = AssetTreeLoader.request;
      const params: LoadAssetsParams = {
        accountId : groupId,
        pageId    : status.page,
        sortOn    : sort,
        filterOn  : filters,
        pageSize
      };
      const results                = await loadAssets(params);
      const {completed, remaining} = this._setGroupStatus(status, results);
      return {assets: results.assets, completed, remaining};
    }
    return Promise.resolve({assets: [], completed: true, remaining: 0});
  }

  /**
   * @method getGroupStatus
   * @param  groupInfo {object} A group info object created via getGroupInfo
   *         which contains a groupKey and groupId property.
   */
  getGroupStatus(groupId, groupKey) {
    let status = this._status[groupId];
    if (!status && groupKey) {
      status = this._status[groupId] = {
        loaded : 0, page : 1, completed : false, groupKey
      };
    }
    return status;
  }

  /**
   * @private
   * @method _setGroupStatus
   */
  _setGroupStatus(status, {totalCount, assets}): Status {
    const retrieved  = status.loaded + assets.length;
    status.loaded    = retrieved;
    status.remaining = totalCount - retrieved;
    if (retrieved >= totalCount) {
      status.completed = true;
    } else {
      status.page = ++status.page;
    }
    return status;
  }

  /**
   * @method requestSearch
   */
  async requestSearch(searchValue: string, currentGlobalGroup, pageSize: number) {
    const param = {
      accountId        : normalizeAccountIds(currentGlobalGroup),
      includeSubGroups : true,
      filterOn         : {
        [AssetSearchableProperty.name] : `*${searchValue}*`
      },
      includeGroups : true,
      pageSize      : pageSize ?? 10000 // this number is subjected to change on Impl. of pagination
    };
    // request asset search
    const {assets} = await loadAssets(param);
    const assetsFound = mapDataToGroups({
      data : assets, type : 'assets'
    });
    // filter asset by display only within the current global group
    return assetsFound.filter(({id}) => currentGlobalGroup[id]);
  };

  /**
   * @method getStatus
   */
  getStatus(): Status {
    return this._status;
  }

  /**
   * @method reset
   */
  reset() {
    this._status = {};
  }

  /**
   * @static
   */
  static request = {
    pageSize : PAGE_SIZE,
    sort     : ['name'],
    filters  : {
      assetType : '-Tool'
    }
  };
};

export default new AssetTreeLoader();
